/**
 * Human-readable elapsed time: seconds for short spans, minutes and seconds for medium ones,
 * hours for long ones.
 *
 *   formatDuration(45)    // '45s'
 *   formatDuration(90)    // '1m 30s'
 *   formatDuration(3661)  // '1h 1m 1s'
 *   formatDuration(null)  // '—'
 *
 * The canonical copy. This logic previously existed twice — in the agent-costs dashboard and
 * the well-spacing job-status page — with the agent-costs copy describing itself as "the
 * dashboard's single source of truth" while mirroring the other. Both now delegate here, so a
 * change to how the product renders a duration happens in one place.
 *
 * A fixed unit reads badly across the range this covers: `0.08 min` for an eight-second job is
 * worse than `8s`, and `252s` for a four-minute one is worse than `4m 12s`. Choosing the unit
 * by magnitude is what makes it legible at both ends.
 *
 * Rounds to whole seconds, so a caller polling on an interval sees the value step rather than
 * drift — which reads as progress rather than noise.
 *
 * @param {number | null | undefined} seconds - Elapsed seconds; null/undefined render as a dash.
 * @return {string} The formatted duration.
 */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '—';

  // Negative input means a clock skew somewhere upstream; show zero rather than '-5s'.
  const rounded = Math.max(0, Math.round(seconds));
  if (rounded < 60) return `${rounded}s`;

  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  if (mins < 60) return `${mins}m ${secs}s`;

  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hours}h ${remainMins}m ${secs}s`;
};
